import React from 'react';
import { Link } from 'react-router-dom';
import './Website.css';
import inboxLogo from './assets/LOGO_1000px.png';
import artboardScan from './assets/Artboard 1.png';
import artboardReport from './assets/Artboard 2.png';
import artboardSecurity from './assets/Artboard 3.png';
import artboardLanguages from './assets/Artboard 4.png';
import artboardDark from './assets/Artboard 5.png';
import casaBadge from './assets/CASA2.png';
import googleCertified from './assets/Google-Certified-Company.jpg';
import madeInUsa from './assets/madeinusa.png';

const chromeStoreUrl = 'https://chromewebstore.google.com/detail/unsubscriber-mass-unsubsc/jpaajppadbkogfmgofandmiglolmecea';

const workflow = [
  {
    label: 'Scan',
    title: 'Find repeat senders fast',
    copy: 'Unsubscriber groups noisy senders so you can review the subscriptions taking over Gmail.',
  },
  {
    label: 'Review',
    title: 'Choose what stays',
    copy: 'Keep important mail untouched while selecting the brands, lists, and notifications you no longer need.',
  },
  {
    label: 'Clean',
    title: 'Unsubscribe and delete',
    copy: 'Process unwanted senders in batches and clear old messages without grinding through Gmail one by one.',
  },
];

const productShots = [
  {
    image: artboardScan,
    alt: 'Unsubscriber sender scan screen',
    title: 'Grouped by sender',
    copy: 'See repeated senders ordered by frequency before you touch anything.',
  },
  {
    image: artboardReport,
    alt: 'Unsubscriber action report screen',
    title: 'After-action reports',
    copy: 'Review what succeeded, what failed, and how much old mail was removed.',
  },
  {
    image: artboardDark,
    alt: 'Unsubscriber dark mode screen',
    title: 'Light and dark mode',
    copy: 'Use the extension comfortably in the Gmail workflow you already have.',
  },
];

export default function LandingPage() {
  return (
    <main className="site-page">
      <section className="hero-section">
        <div className="section-inner hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">Gmail cleanup for people who are done with clutter</p>
            <h1>Unsubscriber turns a crowded inbox into a clean decision list.</h1>
            <p className="hero-lede">
              Scan Gmail, review subscriptions by sender, unsubscribe from the junk, and delete old email backlog before it keeps eating into your Google storage.
            </p>
            <div className="hero-actions">
              <a className="button button-primary" href={chromeStoreUrl} target="_blank" rel="noreferrer">Get Extension on Chrome</a>
            </div>
            <div className="hero-proof" aria-label="Product highlights">
              <span>30 days of free scans</span>
              <span>Batch unsubscribe</span>
              <span>Clear storage-heavy clutter</span>
            </div>
          </div>

          <div className="hero-visual" aria-label="Unsubscriber product preview">
            <div className="inbox-orbit">
              <img src={inboxLogo} alt="Unsubscriber Gmail cleanup icon" />
            </div>
            <div className="sender-panel sender-panel-left">
              <span className="panel-kicker">Detected</span>
              <strong>428</strong>
              <span>subscription emails</span>
            </div>
            <div className="sender-panel sender-panel-right">
              <span className="panel-kicker">Ready</span>
              <strong>91</strong>
              <span>senders to review</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-white">
        <div className="section-inner showcase-section">
          <div className="section-heading">
            <p className="eyebrow">Built for fast review</p>
            <h2>Actual extension screens, not abstract product art.</h2>
            <p>
              The site now leads with the real workflow: sender frequency, batch actions, report status, languages, and dark mode.
            </p>
          </div>
          <div className="screenshot-grid">
            {productShots.map((shot) => (
              <article className="screenshot-card" key={shot.title}>
                <img src={shot.image} alt={shot.alt} />
                <div>
                  <h3>{shot.title}</h3>
                  <p>{shot.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-muted" id="pricing">
        <div className="section-inner product-grid">
          <div className="product-shot">
            <img src={artboardLanguages} alt="Unsubscriber language selector with supported languages" />
          </div>
          <div className="feature-stack">
            <p className="eyebrow">How it works</p>
            <h2>One pass through the inbox. Three clear steps.</h2>
            <div className="workflow-list">
              {workflow.map((item) => (
                <article className="workflow-item" key={item.label}>
                  <span>{item.label}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-white">
        <div className="section-inner storage-section">
          <div>
            <p className="eyebrow">Beat storage pressure</p>
            <h2>Clean Gmail before Google storage pushes you toward an upgrade.</h2>
          </div>
          <div className="storage-copy">
            <div className="storage-alert-card" aria-label="Google storage warning illustration">
              <span className="storage-alert-mark">!</span>
              <div>
                <strong>Google storage almost full</strong>
                <p>Gmail clutter is using space shared with Drive and Photos.</p>
              </div>
            </div>
            <p>
              Google Account storage is shared across Gmail, Drive, and Photos. When years of old sender clutter and email attachments pile up, deleting the right Gmail backlog can help you reclaim space before paying for more cloud storage.
            </p>
            <p>
              Unsubscriber helps you find repeat senders, unsubscribe from the noise, and remove old messages in batches instead of hunting through Gmail one search at a time.
            </p>
          </div>
        </div>
      </section>

      <section className="section section-muted">
        <div className="section-inner pricing-section">
          <div className="pricing-copy">
            <p className="eyebrow">Simple pricing</p>
            <h2>Start with 30 days of free scans. Upgrade inside the extension when you want ongoing cleanup.</h2>
            <p>
              Free scans give you a real look at recent Gmail clutter. The annual plan is for people who want deeper history, repeated maintenance, and heavier inbox cleanup.
            </p>
          </div>
          <div className="pricing-grid">
            <article className="plan-card">
              <span className="plan-label">Free</span>
              <h3>30-day scans</h3>
              <strong>$0</strong>
              <p>Scan and clean the last 30 days of Gmail clutter for free.</p>
              <ul>
                <li>Scan up to 30 days back</li>
                <li>Unsubscribe and delete selected senders</li>
                <li>See how much noisy mail is building up</li>
              </ul>
            </article>
            <article className="plan-card plan-card-featured">
              <span className="plan-label">Best value</span>
              <h3>Powerhouse</h3>
              <strong>$9.99/year</strong>
              <p>Run deeper Gmail cleanup with more room to scan, review, and maintain.</p>
              <ul>
                <li>Unlimited scan history</li>
                <li>Deeper inbox lookback</li>
                <li>Priority for heavy inboxes</li>
              </ul>
              <a className="button button-primary" href={chromeStoreUrl} target="_blank" rel="noreferrer">Get extension</a>
            </article>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="section-inner trust-grid">
          <div>
            <p className="eyebrow eyebrow-light">Verified trust</p>
            <h2>Google Certified and CASA Tier 2 validated.</h2>
            <p>
              Unsubscriber exists to help you take action inside your inbox. The extension is presented with the compliance badges users need to see before trusting an email cleanup tool.
            </p>
          </div>
          <div className="certification-panel">
            <img className="google-cert" src={googleCertified} alt="Google Certified badge" />
            <img className="casa-cert" src={casaBadge} alt="CASA Tier 2 validated by TAC badge" />
            <img className="security-artboard" src={artboardSecurity} alt="CASA Tier 2 certification artboard" />
          </div>
        </div>
      </section>

      <section className="section section-white">
        <div className="section-inner product-grid product-grid-reverse">
          <div>
            <p className="eyebrow">Global inboxes</p>
            <h2>17 languages natively supported.</h2>
            <p>
              The interface supports English, Spanish, French, German, Italian, Portuguese, Chinese, Japanese, Russian, Arabic, Hindi, Bengali, Swedish, Norwegian, Finnish, and more.
            </p>
          </div>
          <img className="wide-artboard" src={artboardLanguages} alt="Supported languages in Unsubscriber" />
        </div>
      </section>

      <section className="section made-section">
        <div className="section-inner made-grid">
          <div>
            <p className="eyebrow">Support and infrastructure</p>
            <h2>Made in the USA, with customer support based in the USA.</h2>
            <p>
              The product is built for Gmail users who want a focused cleanup utility, clear pricing, and direct help when something needs attention.
            </p>
          </div>
          <img src={madeInUsa} alt="Made in USA badge" />
        </div>
      </section>

      <footer className="site-footer">
        <div className="section-inner footer-inner">
          <span>&copy; {new Date().getFullYear()} Unsubscriber. All rights reserved.</span>
          <nav aria-label="Footer">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </div>
      </footer>
    </main>
  );
}
